import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

/**
 * Event helper utilities for parsing and localizing device monitoring events.
 */

export const SEVERITY_CONFIG_MAP = {
  CRITICAL: {
    labelKey: 'filters.critical',
    bg: '#fef2f2',
    color: '#e2453b',
    border: '#fadcd9',
    Icon: ErrorOutlineIcon,
  },
  FATAL: {
    labelKey: 'filters.critical',
    bg: '#fef2f2',
    color: '#e2453b',
    border: '#fadcd9',
    Icon: ErrorOutlineIcon,
  },
  ERROR: {
    labelKey: 'filters.critical',
    bg: '#fef2f2',
    color: '#e2453b',
    border: '#fadcd9',
    Icon: ErrorOutlineIcon,
  },
  HIGH: {
    labelKey: 'filters.critical',
    bg: '#fef2f2',
    color: '#e2453b',
    border: '#fadcd9',
    Icon: ErrorOutlineIcon,
  },
  WARNING: {
    labelKey: 'filters.warning',
    bg: '#fffaf0',
    color: '#d98c1f',
    border: '#f7e6c4',
    Icon: WarningAmberIcon,
  },
  MEDIUM: {
    labelKey: 'filters.warning',
    bg: '#fffaf0',
    color: '#d98c1f',
    border: '#f7e6c4',
    Icon: WarningAmberIcon,
  },
  INFO: {
    labelKey: 'filters.info',
    bg: '#eff6ff',
    color: '#3b82f6',
    border: '#d6e6fd',
    Icon: InfoOutlinedIcon,
  },
  LOW: {
    labelKey: 'filters.info',
    bg: '#eff6ff',
    color: '#3b82f6',
    border: '#d6e6fd',
    Icon: InfoOutlinedIcon,
  },
};

/**
 * Returns the canonical filter level (CRITICAL | WARNING | INFO) for any raw severity.
 * Handles edge cases like new or unconventional backend event severities.
 *
 * @param {string} severity - Raw severity string
 * @returns {string} Canonical level key
 */
export const getCanonicalSeverity = (severity) => {
  const normalized = (severity || '').toUpperCase();
  if (['CRITICAL', 'ERROR', 'FATAL', 'HIGH'].includes(normalized)) {
    return 'CRITICAL';
  }
  if (['WARNING', 'MEDIUM', 'WARN'].includes(normalized)) {
    return 'WARNING';
  }
  if (['INFO', 'LOW', 'NOTICE'].includes(normalized)) {
    return 'INFO';
  }
  return normalized || 'INFO';
};

/**
 * Expands selected UI filter levels into all known backend severity aliases.
 *
 * @param {Array<string>|string} levels - Array of filter levels or single level
 * @returns {string|undefined} Comma-separated query param string
 */
export const expandSeverityParam = (levels) => {
  const list = Array.isArray(levels) ? levels : (levels ? [levels] : []);
  if (!list.length) return undefined;

  const expanded = new Set();
  list.forEach((lvl) => {
    const u = (lvl || '').toUpperCase();
    if (u === 'CRITICAL') {
      ['CRITICAL', 'ERROR', 'FATAL', 'HIGH'].forEach((s) => expanded.add(s));
    } else if (u === 'WARNING') {
      ['WARNING', 'MEDIUM', 'WARN'].forEach((s) => expanded.add(s));
    } else if (u === 'INFO') {
      ['INFO', 'LOW', 'NOTICE'].forEach((s) => expanded.add(s));
    } else if (u) {
      expanded.add(u);
    }
  });

  return expanded.size > 0 ? Array.from(expanded).join(',') : undefined;
};

/**
 * Resolves a severity string to a safe visual configuration and localized label.
 * Includes a safeguard fallback for unexpected or unknown severity levels.
 *
 * @param {string} severity - Raw severity string from event payload
 * @param {Function} t - react-i18next translation function
 * @returns {Object} Config containing { label, bg, color, border, Icon }
 */
export const getSeverityConfig = (severity, t) => {
  const normalized = (severity || '').toUpperCase();
  const knownConfig = SEVERITY_CONFIG_MAP[normalized];

  if (knownConfig) {
    return {
      ...knownConfig,
      label: t ? t(knownConfig.labelKey, normalized) : normalized,
    };
  }

  // Safeguard for unknown severities (e.g. "FATAL", "NOTICE", "EMERGENCY")
  const formattedUnknown = severity
    ? (severity.charAt(0).toUpperCase() + severity.slice(1).toLowerCase())
    : 'Info';

  const i18nKey = `filters.${normalized.toLowerCase()}`;
  const translatedUnknown = t ? t(i18nKey) : formattedUnknown;

  return {
    label: (translatedUnknown && translatedUnknown !== i18nKey) ? translatedUnknown : formattedUnknown,
    bg: '#f3f4f6',
    color: '#4b5563',
    border: '#e5e7eb',
    Icon: InfoOutlinedIcon,
  };
};

export const EVENT_REGEX_PATTERNS = [
  {
    regex: /(?:Sensor illumination degraded|Iluminação do sensor degradada):\s*([A-Za-z0-9_áéíóúÁÉÍÓÚ]+)/i,
    getKeyAndParams: (match) => {
      let rawLevel = (match[1] || 'LOW').toUpperCase();
      if (rawLevel === 'BAIXA') rawLevel = 'LOW';
      else if (rawLevel === 'MÉDIA' || rawLevel === 'MEDIA') rawLevel = 'MEDIUM';
      else if (rawLevel === 'ELEVADA' || rawLevel === 'ALTA') rawLevel = 'HIGH';
      else if (rawLevel === 'INSUFICIENTE') rawLevel = 'INSUFFICIENT';
      return {
        key: 'messages.ILLUMINATION_LOW',
        levelRaw: rawLevel,
      };
    },
  },
  {
    regex: /(?:Sensor (?:displacement|movement|moved) detected|Deslocamento do sensor detetado)/i,
    getKeyAndParams: () => ({
      key: 'messages.SENSOR_MOVED',
    }),
  },
  {
    regex: /(?:Device OFFLINE|Dispositivo offline):\s*(.*)/i,
    getKeyAndParams: (match) => ({
      key: 'messages.DEVICE_OFFLINE',
      params: { reason: match[1] },
    }),
  },
];

/**
 * Formats a device event object or raw string into a localized message using i18next `t`.
 *
 * @param {Object|string} event - The device event object or raw message string
 * @param {Function} t - The react-i18next translation function (namespace 'events')
 * @returns {string} The formatted localized message string
 */
export const formatEventMessage = (event, t) => {
  if (!event) return '—';

  // If passed string directly as legacy fallback
  if (typeof event === 'string') {
    const directKey = `messages.${event}`;
    const directTrans = t(directKey);
    if (directTrans && directTrans !== directKey && !directTrans.endsWith(`.${event}`) && !directTrans.includes('{{')) {
      return directTrans;
    }
    // Try matching raw string against regex patterns
    for (const pattern of EVENT_REGEX_PATTERNS) {
      const match = event.match(pattern.regex);
      if (match) {
        const { key, levelRaw, params: patternParams } = pattern.getKeyAndParams(match);
        const mergedParams = { ...patternParams };
        if (levelRaw) {
          const levelKey = `levels.${levelRaw}`;
          const translatedLevel = t(levelKey);
          mergedParams.level = (translatedLevel && translatedLevel !== levelKey && !translatedLevel.endsWith(`.${levelRaw}`))
            ? translatedLevel
            : levelRaw;
        }
        const translatedReg = t(key, mergedParams);
        if (translatedReg && translatedReg !== key && !translatedReg.endsWith(`.${key.split('.').pop()}`)) {
          return translatedReg;
        }
      }
    }
    return event;
  }

  const { eventType, description, message, details } = event;
  const rawMsg = message || description || eventType || '';

  // Extract params from details JSON if available
  let params = {};
  if (details) {
    if (typeof details === 'object') {
      params = { ...details };
    } else if (typeof details === 'string') {
      try {
        params = JSON.parse(details);
      } catch (e) {
        // ignore parse error
      }
    }
  }

  // Extract missing level or pattern params from rawMsg using EVENT_REGEX_PATTERNS
  if (rawMsg) {
    for (const pattern of EVENT_REGEX_PATTERNS) {
      const match = rawMsg.match(pattern.regex);
      if (match) {
        const { levelRaw, params: patternParams } = pattern.getKeyAndParams(match);
        if (levelRaw && !params.level) {
          params.level = levelRaw;
        }
        if (patternParams) {
          params = { ...patternParams, ...params };
        }
      }
    }
  }

  // Translate level parameter if level is in params (e.g. INSUFFICIENT -> INSUFFICIENT in EN / INSUFICIENTE in PT)
  if (params.level) {
    const levelKey = `levels.${params.level}`;
    const translatedLevel = t(levelKey);
    if (translatedLevel && translatedLevel !== levelKey && !translatedLevel.endsWith(`.${params.level}`)) {
      params.level = translatedLevel;
    }
  }

  // 1. Try translation by eventType (e.g. messages.ILLUMINATION_LOW)
  if (eventType) {
    const typeKey = `messages.${eventType}`;
    const translatedType = t(typeKey, params);
    if (translatedType && translatedType !== typeKey && !translatedType.endsWith(`.${eventType}`) && !translatedType.includes('{{')) {
      return translatedType;
    }
  }

  // 2. Try matching rawMsg against regex patterns for flexible response parsing
  if (rawMsg) {
    for (const pattern of EVENT_REGEX_PATTERNS) {
      const match = rawMsg.match(pattern.regex);
      if (match) {
        const { key, levelRaw, params: patternParams } = pattern.getKeyAndParams(match);
        const mergedParams = { ...params, ...patternParams };
        if (levelRaw && !mergedParams.level) {
          const levelKey = `levels.${levelRaw}`;
          const translatedLevel = t(levelKey);
          mergedParams.level = (translatedLevel && translatedLevel !== levelKey && !translatedLevel.endsWith(`.${levelRaw}`))
            ? translatedLevel
            : levelRaw;
        }
        const translatedReg = t(key, mergedParams);
        if (translatedReg && translatedReg !== key && !translatedReg.endsWith(`.${key.split('.').pop()}`)) {
          return translatedReg;
        }
      }
    }
  }

  // 3. Try translation by exact message/description key (legacy mapping)
  if (rawMsg) {
    const msgKey = `messages.${rawMsg}`;
    const translatedMsg = t(msgKey, params);
    if (translatedMsg && translatedMsg !== msgKey && !translatedMsg.endsWith(`.${rawMsg}`)) {
      return translatedMsg;
    }
  }

  // 4. Fallback to raw string
  return rawMsg || '—';
};
